import { useTranslation } from 'react-i18next';

/**
 * Sefer ekranlarının sayı biçimi (M6f G4-05; plan §0.6 "Sayılar"): birim başına sabit ondalık,
 * dile göre binlik ayırıcı, eksi sayıda U+2212; boş değerde yalnız "—" (birim yazılmaz).
 */
export interface NumberFormat {
  /** En çok ondalık (varsayılan 2). */
  digits?: number;
  /** Ondalık sabit: "520.0", "6.80". */
  fixed?: boolean;
}

export const EMPTY = '—';

export function formatNumber(
  value: number | null | undefined,
  language: string,
  { digits = 2, fixed = false }: NumberFormat = {},
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY;
  const text = new Intl.NumberFormat(language === 'tr' ? 'tr-TR' : 'en-US', {
    minimumFractionDigits: fixed ? digits : 0,
    maximumFractionDigits: digits,
  }).format(value);
  return text.replace(/^-/, '−');
}

/** Değer ve birim; değer boşsa ("—") birim yazılmaz. */
export function withUnit(text: string, unit: string): string {
  return text === EMPTY ? EMPTY : `${text} ${unit}`;
}

/**
 * Ekrandaki sayı biçimi. `n(value, 1)` en çok bir ondalık; `n(value, { digits: 1, fixed: true })`
 * sabit; `n.unit(value, 'kn', 1)` birimli.
 */
export function useNumber() {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const n = (value: number | null | undefined, format: number | NumberFormat = 2) =>
    formatNumber(value, language, typeof format === 'number' ? { digits: format } : format);
  n.unit = (value: number | null | undefined, unit: string, format: number | NumberFormat = 2) =>
    withUnit(n(value, format), unit);
  return n;
}
